import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// The back office's words for the Search page (ADR-028 revision 1 point 5; S33.14, S33.03).
// `ops-copy.ts` puts them together with the rest into the one catalogue the screens read.

export type SearchCopyCatalog = typeof EN;

const EN = {
  titleSearch: "Search · Back office · {product}",
  searchPageTitle: "Search",
  searchPageDescription:
    "Google web search for the website, from the days the hourly task stored. Searches, not people.",
  searchTilesTitle: "The last 28 final days",
  searchWindow: "{from} to {to}, against {previousFrom} to {previousTo}",
  searchLatestDay: "Newest stored day: {day}",
  searchFillingTitle: "{stored} of 28 days are stored so far",
  searchFillingDescription:
    "The task stores each day once Search Console calls it final, three days later, and fills up to seven missed days a run.",
  searchEmptyTitle: "No Search Console day is stored yet",
  searchEmptyDescription:
    "The hourly task stores each final day, three days after it ends. The first days appear within the hour of the key being set.",
  searchErrorTitle: "The Search report did not load",
  searchErrorDescription: "Reload the page. If it keeps failing, the api's log names the cause.",
  searchClicks: "Clicks",
  searchImpressions: "Impressions",
  searchCtr: "CTR",
  searchPosition: "Average position",
  searchChange: "{change} against the 28 days before ({value})",
  searchPositionChange: "Was {value} in the 28 days before",
  searchNothingBefore: "Nothing stored for the 28 days before",
  searchDailyTitle: "Clicks per day",
  searchDailyDescription: "A day the task has not stored yet reads 0.",
  searchDailyUnit: "clicks",
  searchStrikingTitle: "Striking distance",
  searchStrikingDescription:
    "Queries averaging position 5 to 15 with at least 20 impressions. The weekly review copies them into the keyword map.",
  searchStrikingEmpty: "No query is in striking distance in these 28 days.",
  searchTopQueriesTitle: "Top queries",
  searchTopPagesTitle: "Top pages",
  searchTopEmpty: "Nothing was shown in these 28 days.",
  searchColumnQuery: "Query",
  searchColumnPage: "Page",
  searchColumnPosition: "Position",
  searchColumnClicks: "Clicks",
  searchColumnImpressions: "Impressions",
  searchColumnCtr: "CTR",
  openSearchReportAction: "Open the Search report",
} as const;

const ES: Translation<SearchCopyCatalog> = {
  titleSearch: "Búsqueda · Back office · {product}",
  searchPageTitle: "Búsqueda",
  searchPageDescription:
    "La búsqueda web de Google para el sitio, de los días que guardó la tarea horaria. Son búsquedas, no personas.",
  searchTilesTitle: "Los últimos 28 días finales",
  searchWindow: "Del {from} al {to}, frente al {previousFrom} al {previousTo}",
  searchLatestDay: "Último día guardado: {day}",
  searchFillingTitle: "Hay {stored} de 28 días guardados por ahora",
  searchFillingDescription:
    "La tarea guarda cada día cuando Search Console lo da por final, tres días después, y recupera hasta siete días perdidos por pasada.",
  searchEmptyTitle: "Todavía no hay ningún día de Search Console guardado",
  searchEmptyDescription:
    "La tarea horaria guarda cada día final, tres días después de que termina. Los primeros días aparecen en la hora siguiente a configurar la clave.",
  searchErrorTitle: "El informe de Búsqueda no cargó",
  searchErrorDescription: "Recarga la página. Si sigue fallando, el log de la api dice la causa.",
  searchClicks: "Clics",
  searchImpressions: "Impresiones",
  searchCtr: "CTR",
  searchPosition: "Posición media",
  searchChange: "{change} frente a los 28 días anteriores ({value})",
  searchPositionChange: "Era {value} en los 28 días anteriores",
  searchNothingBefore: "Nada guardado de los 28 días anteriores",
  searchDailyTitle: "Clics por día",
  searchDailyDescription: "Un día que la tarea aún no guardó se lee como 0.",
  searchDailyUnit: "clics",
  searchStrikingTitle: "A punto de entrar",
  searchStrikingDescription:
    "Búsquedas con posición media de 5 a 15 y al menos 20 impresiones. La revisión semanal las copia al mapa de palabras clave.",
  searchStrikingEmpty: "Ninguna búsqueda está a punto de entrar en estos 28 días.",
  searchTopQueriesTitle: "Búsquedas principales",
  searchTopPagesTitle: "Páginas principales",
  searchTopEmpty: "No se mostró nada en estos 28 días.",
  searchColumnQuery: "Búsqueda",
  searchColumnPage: "Página",
  searchColumnPosition: "Posición",
  searchColumnClicks: "Clics",
  searchColumnImpressions: "Impresiones",
  searchColumnCtr: "CTR",
  openSearchReportAction: "Abrir el informe de Búsqueda",
};

export const SEARCH_CATALOGS: Catalogs<SearchCopyCatalog> = { en: EN, es: ES };
